import React, { useState } from 'react';
import axios from 'axios';
import { FaCopy, FaCheck, FaTimes, FaLink } from 'react-icons/fa';

const EXPIRATION_OPTIONS = [
  { value: 24, label: '24 Hours' },
  { value: 168, label: '7 Days' },
  { value: 720, label: '30 Days' },
];

const ShareLinkModal = ({ isOpen, onClose, file, module }) => {
  const [shareLink, setShareLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [expiresAt, setExpiresAt] = useState('');
  const [selectedExpiration, setSelectedExpiration] = useState(168);

  const generateShareLink = async () => {
    setLoading(true);
    setError('');
    setCopied(false);
    
    try {
      const userId = localStorage.getItem('userId');
      const response = await axios.post('/api/create-share-link', {
        fileId: file._id,
        module: module,
        userId: userId,
        hoursValid: selectedExpiration
      });
      
      if (response.data.link) {
        setShareLink(response.data.link);
        setExpiresAt(response.data.expires_at);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate share link');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = shareLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setShareLink('');
    setError('');
    setCopied(false);
    setSelectedExpiration(168);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={handleClose}
    >
      <div
        style={{
          background: 'white',
          padding: 24,
          borderRadius: 12,
          minWidth: 400,
          maxWidth: 500,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#224228' }}>
            <FaLink style={{ marginRight: 8 }} />
            Share File
          </h3>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              color: '#666'
            }}
          >
            <FaTimes size={18} />
          </button>
        </div>

        <div style={{ background: '#f9f9f9', padding: 12, borderRadius: 8, marginBottom: 16 }}>
          <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>File Name:</div>
          <div style={{ fontSize: 14, fontWeight: 500 }}>{file?.original_name || file?.name || 'Unknown'}</div>
        </div>

        {!shareLink ? (
          <div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 14, color: '#666', marginBottom: 8 }}>Link Expiration:</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {EXPIRATION_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSelectedExpiration(option.value)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      background: selectedExpiration === option.value ? '#224228' : '#f0f0f0',
                      color: selectedExpiration === option.value ? 'white' : '#333',
                      border: selectedExpiration === option.value ? 'none' : '1px solid #ddd',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 500,
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={generateShareLink}
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: loading ? '#ccc' : '#224228',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              {loading ? 'Generating...' : 'Generate Share Link'}
            </button>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Share Link (valid for {EXPIRATION_OPTIONS.find(o => o.value === selectedExpiration)?.label || '24 Hours'}):</div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                background: '#f0f0f0', 
                borderRadius: 6, 
                padding: '8px 12px',
                border: '1px solid #ddd'
              }}>
                <input
                  type="text"
                  value={shareLink}
                  readOnly
                  style={{
                    flex: 1,
                    border: 'none',
                    background: 'transparent',
                    fontSize: 12,
                    outline: 'none',
                  }}
                />
                <button
                  onClick={copyToClipboard}
                  style={{
                    background: copied ? '#22c55e' : '#224228',
                    color: 'white',
                    border: 'none',
                    borderRadius: 4,
                    padding: '6px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 12,
                  }}
                >
                  {copied ? <FaCheck size={12} /> : <FaCopy size={12} />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            
            {expiresAt && (
              <div style={{ fontSize: 12, color: '#666' }}>
                Expires: {new Date(expiresAt).toLocaleString()}
              </div>
            )}
          </div>
        )}

        {error && (
          <div style={{ 
            marginTop: 12, 
            padding: 8, 
            background: '#fee2e2', 
            color: '#dc2626', 
            borderRadius: 4,
            fontSize: 12 
          }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareLinkModal;
